import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { AlertIcon, AppShell, Avatar, Banner, Button, IconButton, LogoutIcon, ShieldIcon, UpdateIcon } from 'armxlib'
import { useNavigate } from 'react-router'
import { useMe } from '../features/account/hooks'
import { signOut } from '../features/auth/api'
import { en } from '../i18n/en'
import { ROUTES } from '../routes'
import { useOnline } from './useOnline'
import { useUpdateAvailable } from './version'

/** Props of {@link Shell}. */
export interface ShellProps {
  /** Bar pinned to the bottom, for the message field. */
  bottomBar?: ReactNode
  /** Shown after the content. */
  footer?: ReactNode
  /** Widest the content gets, for pages that need more room than the chat. */
  width?: string
  /** The page. */
  children: ReactNode
}

/**
 * Frame of the signed-in pages: header with the moderation, account and log
 * out buttons, and the offline and update banners.
 */
export function Shell({ bottomBar, footer, width, children }: ShellProps) {
  const me = useMe()
  const navigate = useNavigate()
  const online = useOnline()
  const updateAvailable = useUpdateAvailable()
  const [updateDismissed, setUpdateDismissed] = useState(false)

  const banner = !online ? (
    <Banner tone="warning" icon={<AlertIcon />}>
      {en.chat.offline}
    </Banner>
  ) : updateAvailable && !updateDismissed ? (
    <Banner
      icon={<UpdateIcon />}
      action={
        <Button compact variant="primary" onClick={() => window.location.reload()}>
          {en.update.reload}
        </Button>
      }
      onDismiss={() => setUpdateDismissed(true)}
    >
      {en.update.available}
    </Banner>
  ) : undefined

  async function logOut() {
    await signOut()
    navigate(ROUTES.login.path, { replace: true })
  }

  return (
    <AppShell
      ascii
      brand={en.site.name}
      brandHref={ROUTES.chat.path}
      banner={banner}
      bottomBar={bottomBar}
      footer={footer}
      style={width ? ({ '--armx-shell-width': width } as CSSProperties) : undefined}
      actions={
        <>
          {me.data?.moderator && (
            <IconButton href={ROUTES.moderation.path} label={en.nav.moderation}>
              <ShieldIcon />
            </IconButton>
          )}
          <IconButton href={ROUTES.account.path} label={en.nav.account}>
            <Avatar name={me.data?.username ?? ' '} size="1.75rem" />
          </IconButton>
          <IconButton label={en.nav.logOut} onClick={() => void logOut()}>
            <LogoutIcon />
          </IconButton>
        </>
      }
    >
      {children}
    </AppShell>
  )
}
